import { AccountsPanel } from './features/marketing/AccountsPanel'
import { AccountPage } from './features/marketing/AccountPage'
import { DirectionPage } from './features/marketing/DirectionPage'
import { DirectionsPanel } from './features/marketing/DirectionsPanel'
import { Section, useSection, type SectionId } from './features/layout/Section'
import { PublicationsPanel } from './features/marketing/PublicationsPanel'
import { LinksPanel } from './features/marketing/LinksPanel'
import { Compass, LayoutDashboard,
  LogOut, PanelLeftClose, PanelLeftOpen, Send, Settings, Smartphone, Sprout,
  type LucideIcon,
} from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { Navigate, NavLink, Route, Routes, useSearchParams } from 'react-router-dom'
import { Dashboard } from './features/dashboard/Dashboard'
import { MyKeysPanel } from './features/keys/MyKeysPanel'
import { ServiceKeysPanel } from './features/keys/ServiceKeysPanel'

import type { Device } from './api/types'
import { DeviceList } from './features/devices/DeviceList'
import { DistributionsPanel } from './features/distributions/DistributionsPanel'
import { HubsPanel } from './features/hubs/HubsPanel'
import { LogPanel } from './features/logs/LogPanel'
import { PostsPanel } from './features/posts/PostsPanel'
import { SessionPanel } from './features/session/SessionPanel'
import { ScenariosPanel } from './features/scenarios/ScenariosPanel'
import { SourcesPanel } from './features/sources/SourcesPanel'
import { UsersPanel } from './features/users/UsersPanel'
import { VideosPanel } from './features/videos/VideosPanel'
import { LanguageSwitcher, useT } from './i18n'
import { useApp } from './lib/context'
import { Card, ThemeToggle } from './ui/components'
import { Person } from './ui/Person'

export function App() {
  return (
    <div className="app" id="top">
      <Sidebar />
      <div className="main">
        <Routes>
          {/* Five sections (owner, 2026-10-12): Overview, Phones, Directions, Posting, Settings. A section's
              pages sit behind the tabs on its top (`Section`); the old addresses all still open. */}
          <Route path="/" element={<div className="page col"><Dashboard /></div>} />
          <Route path="/phones" element={<Workspace />} />
          <Route path="/directions" element={<Section section="directions"><DirectionsPanel /></Section>} />
          <Route path="/directions/:id" element={<DirectionPage />} />
          <Route path="/accounts" element={<Section section="directions"><AccountsPanel /></Section>} />
          <Route path="/accounts/:id" element={<AccountPage />} />
          <Route path="/publications" element={<Section section="directions"><PublicationsPanel /></Section>} />
          <Route path="/links" element={<Section section="directions"><LinksPanel /></Section>} />
          <Route path="/posting" element={<Navigate to="/posts" replace />} />
          <Route path="/posts" element={<Section section="posting"><PostsPanel /></Section>} />
          <Route path="/posts/:id" element={<Section section="posting"><PostsPanel /></Section>} />
          <Route path="/distributions" element={<Section section="posting"><DistributionsPanel /></Section>} />
          <Route path="/distributions/:id" element={<Section section="posting"><DistributionsPanel /></Section>} />
          <Route path="/videos" element={<Section section="posting"><VideosPanel /></Section>} />
          <Route path="/settings" element={<Navigate to="/machines" replace />} />
          <Route path="/machines" element={<Section section="settings"><HubsPanel /></Section>} />
          <Route path="/sources" element={<Section section="posting"><SourcesPanel /></Section>} />
          <Route path="/scenarios" element={<Section section="settings"><ScenariosPanel /></Section>} />
          <Route path="/users" element={<Section section="settings"><UsersPanel /></Section>} />
          <Route path="/keys" element={<Section section="settings"><ServiceKeysPanel /></Section>} />
          <Route path="/me/keys" element={<Section section="settings"><MyKeysPanel /></Section>} />
        </Routes>
      </div>
    </div>
  )
}

/** Remembered per browser: a person who collapsed the menu wants it collapsed next time too. */
const MENU_KEY = 'tthub_menu'

/**
 * **The navigation is a sidebar** (redesign stage 1, ported from Device Lab on 2026-10-06). Eight
 * sections in one top row read as one undifferentiated list, wrapped to two rows on a laptop and took
 * height from the phone picture — the one thing here that needs height. Grouped by what a person is
 * doing — the daily work (phones, posts, pushes, videos) and the setup behind it (sources, scenarios,
 * machines) — with your Claude and who you are at the foot.
 *
 * **Open unless the person closed it**; once somebody chooses, their choice wins on every screen (a
 * menu that opens and closes by itself is one you cannot learn). A collapsed menu **peeks open under
 * the pointer**, over the page rather than pushing it. On a phone-width window it is a strip of icons
 * across the top.
 */
function Sidebar() {
  const { auth, signOut } = useApp()
  const t = useT()
  const user = auth.user
  const [chosen, setChosen] = useState<'open' | 'closed' | null>(() => {
    try {
      const v = localStorage.getItem(MENU_KEY)
      return v === 'open' || v === 'closed' ? v : null
    } catch {
      return null
    }
  })
  const collapsed = chosen === 'closed'
  const [peek, setPeek] = useState(false)
  const peekTimer = useRef<number | null>(null)
  const enter = () => {
    if (!collapsed) return
    if (peekTimer.current) window.clearTimeout(peekTimer.current)
    //: a short delay, so a pointer passing through to the page's own left edge does not flash it open
    peekTimer.current = window.setTimeout(() => setPeek(true), 150)
  }
  const leave = () => {
    if (peekTimer.current) window.clearTimeout(peekTimer.current)
    setPeek(false)
  }
  const shown = collapsed && !peek
  const toggle = () => {
    const next = collapsed ? 'open' : 'closed'
    setPeek(false)
    setChosen(next)
    try {
      localStorage.setItem(MENU_KEY, next)
    } catch {
      //: storage denied — the menu still toggles for this visit
    }
  }
  const here = useSection()
  //: a section is lit whichever of its pages is open — the menu always says where you are
  const item = (to: string, Icon: LucideIcon, label: string, section: SectionId) => (
    <NavLink to={to} title={shown ? label : undefined} aria-label={label}
             className={() => (here === section ? 'active' : '')}>
      <Icon className="ico" size={17} aria-hidden />
      <span className="nav-label">{label}</span>
    </NavLink>
  )

  return (
    <>
      {/* Holds the collapsed width: a collapsed menu floats, peeking or not, so its slide moves nothing. */}
      {collapsed && <div className="sidebar-spacer" aria-hidden />}
      <aside className={`sidebar topbar${shown ? ' is-collapsed' : ''}${collapsed && peek ? ' is-peek' : ''}`}
             onMouseEnter={enter} onMouseLeave={leave}>
        <div className="sidebar-head">
          <span className="brand">
            <span className="mark"><Sprout className="ico" size={15} aria-hidden /></span>
            <span className="nav-label">Ferma Hub</span>
          </span>
          <button className="ghost icon sidebar-toggle" onClick={toggle}
                  title={collapsed ? t.nav.expand : t.nav.collapse}
                  aria-label={collapsed ? t.nav.expand : t.nav.collapse} aria-expanded={!collapsed}>
            {collapsed ? <PanelLeftOpen className="ico" size={16} aria-hidden />
                       : <PanelLeftClose className="ico" size={16} aria-hidden />}
          </button>
        </div>
        <nav className="nav">
          {item('/', LayoutDashboard, t.nav.overview, 'overview')}
          {item('/phones', Smartphone, t.nav.phones, 'phones')}
          {item('/directions', Compass, t.nav.directions, 'directions')}
          {item('/posts', Send, t.nav.posting, 'posting')}
          {item('/machines', Settings, t.nav.settings, 'settings')}
        </nav>
        <div className="sidebar-foot">
          <span className="sidebar-who" title={user ? t.nav.signedInAs(user.email) : t.nav.withToken}>
            {user ? <Person who={user.email} /> : <span className="pill">{t.nav.serviceToken}</span>}
          </span>
          <span className="sidebar-tools">
            <LanguageSwitcher />
            <ThemeToggle />
            <button className="ghost icon" onClick={signOut} title={t.nav.signOut} aria-label={t.nav.signOut}>
              <LogOut className="ico" size={16} aria-hidden />
            </button>
          </span>
        </div>
      </aside>
    </>
  )
}

function Workspace() {
  const t = useT()
  const [device, setDevice] = useState<Device | null>(null)
  const [sessionId, setSessionId] = useState<string | null>(null)
  const { api } = useApp()
  const [params, setParams] = useSearchParams()
  //: `/phones?device=<id>` — the dashboard's "Open the phone" lands here with that phone picked
  const wanted = params.get('device')
  useEffect(() => {
    if (!wanted) return
    let gone = false
    api.device(wanted).then((d) => { if (!gone) setDevice(d) }).catch(() => {}).finally(() => {
      if (!gone) setParams({}, { replace: true })
    })
    return () => { gone = true }
  }, [api, wanted, setParams])

  return (
    <div className="page split">
      <div className="col">
        <DeviceList
          selectedId={device?.device_id ?? null}
          currentSessionId={sessionId}
          onPick={(d) => {
            setDevice(d)
            //: **To the phone** (owner, 2026-10-06): with many phones the one picked is often far down
            //: the list, and the picture opens at the top of the page.
            window.scrollTo({ top: 0, behavior: 'smooth' })
          }}
        />
      </div>
      <div className="col">
        {device ? (
          <SessionPanel
            key={device.device_id}
            device={device}
            onClosed={() => setDevice(null)}
            onSessionChanged={setSessionId}
          />
        ) : (
          <Card>
            <div className="hero">
              <span className="glyph"><Smartphone className="ico" size={26} aria-hidden /></span>
              <strong>{t.session.pickTitle}</strong>
              <p>{t.session.pickBody}</p>
              <p className="tiny dim">{t.session.pickNote}</p>
            </div>
          </Card>
        )}
        <LogPanel sessionId={sessionId} />
      </div>
    </div>
  )
}
